import { useState, useEffect } from 'react';
import { Mail, MessageSquare, Save, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { getSettings, updateSettings } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    email_notifications_enabled: false,
    sms_notifications_enabled: false,
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    sms_api_key: '',
    sms_sender: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Password change state
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getSettings();
        setSettings(response.data);
      } catch (error) {
        toast.error('Ayarlar yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      toast.success('Ayarlar kaydedildi');
    } catch (error) {
      toast.error('Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalı');
      return;
    }

    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', null, {
        params: {
          old_password: passwordData.old_password,
          new_password: passwordData.new_password
        }
      });
      toast.success('Şifre başarıyla değiştirildi');
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre değiştirme başarısız');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 page-enter max-w-3xl" data-testid="admin-settings">
      {/* Password Change */}
      <Card className="bg-card border-white/10">
        <CardHeader className="border-b border-white/10">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-primary" />
            <div>
              <CardTitle>Şifre Değiştir</CardTitle>
              <CardDescription>Yönetici hesap şifrenizi değiştirin</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="old_password">Mevcut Şifre</Label>
              <div className="relative">
                <Input
                  id="old_password"
                  type={showPasswords.old ? 'text' : 'password'}
                  value={passwordData.old_password}
                  onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                  placeholder="Mevcut şifreniz"
                  required
                  className="bg-secondary border-white/10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, old: !showPasswords.old })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPasswords.old ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new_password">Yeni Şifre</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    placeholder="Yeni şifre (min. 6 karakter)"
                    required
                    minLength={6}
                    className="bg-secondary border-white/10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Şifre Tekrar</Label>
                <div className="relative">
                  <Input
                    id="confirm_password"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    placeholder="Yeni şifre tekrar"
                    required
                    className="bg-secondary border-white/10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <Button type="submit" disabled={changingPassword} className="mt-2">
              {changingPassword ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Lock className="w-4 h-4 mr-2" />
              )}
              Şifreyi Değiştir
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Email Settings */}
      <Card className="bg-card border-white/10">
        <CardHeader className="border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-primary" />
              <div>
                <CardTitle>E-posta Bildirimleri</CardTitle>
                <CardDescription>SMTP ayarları ve e-posta bildirimleri</CardDescription>
              </div>
            </div>
            <Switch
              checked={settings.email_notifications_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, email_notifications_enabled: checked })}
            />
          </div>
        </CardHeader>
        <CardContent className={`space-y-4 pt-6 ${!settings.email_notifications_enabled && 'opacity-50 pointer-events-none'}`}>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="smtp_host">SMTP Sunucu</Label>
              <Input
                id="smtp_host"
                value={settings.smtp_host || ''}
                onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                placeholder="smtp.example.com"
                className="bg-secondary border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_port">Port</Label>
              <Input
                id="smtp_port"
                type="number"
                value={settings.smtp_port || 587}
                onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })}
                className="bg-secondary border-white/10"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="smtp_user">Kullanıcı Adı</Label>
              <Input
                id="smtp_user"
                value={settings.smtp_user || ''}
                onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                placeholder="user@example.com"
                className="bg-secondary border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_password">Şifre</Label>
              <Input
                id="smtp_password"
                type="password"
                value={settings.smtp_password || ''}
                onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                className="bg-secondary border-white/10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SMS Settings */}
      <Card className="bg-card border-white/10">
        <CardHeader className="border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-accent" />
              <div>
                <CardTitle>SMS Bildirimleri</CardTitle>
                <CardDescription>SMS API ayarları ve bildirimleri</CardDescription>
              </div>
            </div>
            <Switch
              checked={settings.sms_notifications_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, sms_notifications_enabled: checked })}
            />
          </div>
        </CardHeader>
        <CardContent className={`space-y-4 pt-6 ${!settings.sms_notifications_enabled && 'opacity-50 pointer-events-none'}`}>
          <div className="space-y-2">
            <Label htmlFor="sms_api_key">API Anahtarı</Label>
            <Input
              id="sms_api_key"
              type="password"
              value={settings.sms_api_key || ''}
              onChange={(e) => setSettings({ ...settings, sms_api_key: e.target.value })}
              placeholder="API anahtarınız"
              className="bg-secondary border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sms_sender">Gönderici Adı</Label>
            <Input
              id="sms_sender"
              value={settings.sms_sender || ''}
              onChange={(e) => setSettings({ ...settings, sms_sender: e.target.value })}
              placeholder="FIRMABURADA"
              className="bg-secondary border-white/10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          {saving ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Ayarları Kaydet
        </Button>
      </div>
    </div>
  );
}
